import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { withTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { createProprety } from "../../helpers/requests";
import Breadcrumbs from "../../components/Common/Breadcrumb";

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

// Custom marker icon
const customMarkerIcon = L.icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  shadowSize: [41, 41],
});

// Property types as visual cards instead of a flat select
const PROPERTY_TYPES = [
  { value: "HOTEL", label: "Hôtel", icon: "mdi-bed", desc: "Établissement hôtelier" },
  { value: "DORMITORY", label: "Auberge", icon: "mdi-bunk-bed", desc: "Hébergement partagé" },
  { value: "HOUSE", label: "Maison", icon: "mdi-home-variant", desc: "Maison ou villa" },
];

const SetifCoordinates = [36.1901, 5.4101];

// ── Map handler ──────────────────────────────────────────────────────
function MapHandler({ coordinates, onCoordinateChange }) {
  const map = useMap();

  useEffect(() => {
    map.setView(coordinates, 13);
  }, [coordinates, map]);

  useEffect(() => {
    const handleMapClick = (e) => {
      onCoordinateChange([e.latlng.lat, e.latlng.lng]);
    };
    map.on("click", handleMapClick);
    return () => map.off("click", handleMapClick);
  }, [map, onCoordinateChange]);

  return (
    <Marker position={coordinates} icon={customMarkerIcon}>
      <Popup>Emplacement sélectionné</Popup>
    </Marker>
  );
}

// ── Page ────────────────────────────────────────────────────────────
const PropertyCreate = (props) => {
  const [formData, setFormData] = useState({
    owner: "",
    password: "",
    confirmPassword: "",
    code: "",
    type: "HOTEL",
    images: [],
    name: "",
    name_ar: "",
    description: "",
    description_ar: "",
    location: "",
    coordinates: SetifCoordinates,
    services: [],
    state: "",
    phonenumber: "",
    email: "",
    startprice: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [imagePreview, setImagePreview] = useState([]);
  const [serviceInput, setServiceInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setField = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const validateForm = () => {
    const f = formData;

    if (!f.owner.trim()) return Swal.fire("Erreur", "Le champ propriétaire est obligatoire.", "error"), false;
    if (!f.phonenumber.trim() || !/^\d+$/.test(f.phonenumber))
      return Swal.fire("Erreur", "Numéro de téléphone invalide.", "error"), false;
    if (!f.email.trim() || !/^\S+@\S+\.\S+$/.test(f.email))
      return Swal.fire("Erreur", "Adresse e-mail invalide.", "error"), false;
    if (!f.password) return Swal.fire("Erreur", "Le mot de passe est obligatoire.", "error"), false;
    if (f.password !== f.confirmPassword)
      return Swal.fire("Erreur", "Les mots de passe ne correspondent pas.", "error"), false;
    if (!f.code.trim()) return Swal.fire("Erreur", "Le code est obligatoire.", "error"), false;
    if (!f.name.trim()) return Swal.fire("Erreur", "Le nom de la propriété est obligatoire.", "error"), false;
    if (!f.name_ar.trim()) return Swal.fire("Erreur", "الاسم بالعربية مطلوب", "error"), false;
    if (!f.state.trim()) return Swal.fire("Erreur", "Le champ État est obligatoire.", "error"), false;
    if (!f.startprice || isNaN(f.startprice) || parseFloat(f.startprice) <= 0)
      return Swal.fire("Erreur", "Prix de départ invalide.", "error"), false;
    if (!f.description.trim()) return Swal.fire("Erreur", "La description est obligatoire.", "error"), false;
    if (!f.description_ar.trim()) return Swal.fire("Erreur", "الوصف بالعربية مطلوب", "error"), false;
    if (!f.type) return Swal.fire("Erreur", "Le type de propriété est obligatoire.", "error"), false;
    if (!f.location.trim()) return Swal.fire("Erreur", "La localisation est obligatoire.", "error"), false;
    if (f.coordinates.length !== 2 || isNaN(f.coordinates[0]) || isNaN(f.coordinates[1]))
      return Swal.fire("Erreur", "Les coordonnées sont invalides.", "error"), false;
    if (!f.images.length) return Swal.fire("Erreur", "Au moins une image est requise.", "error"), false;
    if (!f.services.length) return Swal.fire("Erreur", "Au moins un service est requis.", "error"), false;

    return true;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, images: [...prev.images, ...files] }));
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreview((prev) => [...prev, ...previews]);
    e.target.value = ""; // allow re-uploading the same file
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    URL.revokeObjectURL(imagePreview[index]);
    setImagePreview((prev) => prev.filter((_, i) => i !== index));
  };

  const addService = () => {
    const v = serviceInput.trim();
    if (!v) return;
    if (formData.services.includes(v)) {
      setServiceInput("");
      return;
    }
    setFormData((prev) => ({ ...prev, services: [...prev.services, v] }));
    setServiceInput("");
  };

  const removeService = (idx) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx),
    }));
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      Swal.fire("Erreur", "Veuillez entrer un lieu à rechercher.", "error");
      return;
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchTerm
        )}`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        setFormData((prev) => ({
          ...prev,
          coordinates: [parseFloat(lat), parseFloat(lon)],
          location: display_name,
        }));
      } else {
        Swal.fire("Erreur", "Aucun résultat trouvé pour ce lieu.", "error");
      }
    } catch (error) {
      Swal.fire("Erreur", "Impossible de récupérer les données.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "images") {
        formData.images.forEach((img) => data.append("images", img));
      } else if (key === "services") {
        data.append(key, JSON.stringify(formData[key]));
      } else if (key === "coordinates") {
        data.append("latitude", formData.coordinates[0]);
        data.append("longitude", formData.coordinates[1]);
      } else {
        data.append(key, formData[key]);
      }
    });

    setLoading(true);
    try {
      await createProprety(data);
      Swal.fire({
        icon: "success",
        title: "Propriété créée",
        text: "L'établissement a été enregistré avec succès.",
      }).then(() => {
        window.location.href = "/proprety";
      });
    } catch (error) {
      setLoading(false);
      const dup =
        error?.response?.status === 400 &&
        error?.response?.data?.message === "Property with this code already exists";
      Swal.fire(
        "Erreur",
        dup
          ? "Une propriété avec ce code existe déjà. Choisissez un autre code."
          : "Une erreur est survenue lors de la création.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Propriétés")}
            breadcrumbItem={props.t("Nouvelle propriété")}
          />

          <Form onSubmit={handleSubmit}>
            {/* ─── 1. Property type — visual cards ────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-shape-outline text-primary me-2" />
                        Type de propriété
                      </h5>
                      <p className="text-muted mb-0">
                        Sélectionnez la catégorie qui correspond à votre établissement.
                      </p>
                    </div>
                    <Row>
                      {PROPERTY_TYPES.map((t) => {
                        const active = formData.type === t.value;
                        return (
                          <Col md={4} key={t.value}>
                            <button
                              type="button"
                              onClick={() => setField("type", t.value)}
                              className={`w-100 text-start border rounded-3 p-3 d-flex align-items-center gap-3 ${
                                active ? "border-primary bg-primary-subtle" : "border-light bg-white"
                              }`}
                              style={{ cursor: "pointer", transition: "all 0.15s" }}
                            >
                              <div
                                className={`avatar-sm rounded-circle ${
                                  active ? "bg-primary" : "bg-light"
                                }`}
                              >
                                <span
                                  className={`avatar-title rounded-circle ${
                                    active ? "bg-primary text-white" : "bg-light text-primary"
                                  }`}
                                >
                                  <i className={`mdi ${t.icon} font-size-22`} />
                                </span>
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-1">{t.label}</h6>
                                <small className="text-muted">{t.desc}</small>
                              </div>
                              {active && (
                                <i className="mdi mdi-check-circle text-primary font-size-20" />
                              )}
                            </button>
                          </Col>
                        );
                      })}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 2. Owner credentials ───────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-4">
                      <h5 className="mb-1">
                        <i className="mdi mdi-account-key-outline text-primary me-2" />
                        Compte propriétaire
                      </h5>
                      <p className="text-muted mb-0">
                        Identifiants utilisés par le propriétaire pour se connecter au portail hôtel.
                      </p>
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Propriétaire <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-account" /></InputGroupText>
                            <Input
                              name="owner"
                              value={formData.owner}
                              onChange={handleChange}
                              placeholder="Nom du propriétaire ou identifiant"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Numéro de téléphone <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-phone" /></InputGroupText>
                            <Input
                              name="phonenumber"
                              value={formData.phonenumber}
                              onChange={handleChange}
                              placeholder="0555123456"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>E-mail <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-email-outline" /></InputGroupText>
                            <Input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="contact@hotel.com"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Code <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-pound" /></InputGroupText>
                            <Input
                              name="code"
                              value={formData.code}
                              onChange={handleChange}
                              placeholder="HATL001"
                            />
                          </InputGroup>
                          <small className="text-muted">
                            Identifiant interne unique (en majuscules conseillé).
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Mot de passe <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-lock-outline" /></InputGroupText>
                            <Input
                              type={showPwd ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="Au moins 8 caractères"
                            />
                            <Button
                              type="button"
                              color="light"
                              onClick={() => setShowPwd((v) => !v)}
                            >
                              <i className={`mdi ${showPwd ? "mdi-eye-off" : "mdi-eye"}`} />
                            </Button>
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Confirmer le mot de passe <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-lock-check-outline" /></InputGroupText>
                            <Input
                              type={showConfirmPwd ? "text" : "password"}
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                            />
                            <Button
                              type="button"
                              color="light"
                              onClick={() => setShowConfirmPwd((v) => !v)}
                            >
                              <i className={`mdi ${showConfirmPwd ? "mdi-eye-off" : "mdi-eye"}`} />
                            </Button>
                          </InputGroup>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 3. Identity ────────────────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-4">
                      <h5 className="mb-1">
                        <i className="mdi mdi-card-text-outline text-primary me-2" />
                        Identité de la propriété
                      </h5>
                      <p className="text-muted mb-0">
                        Nom, descriptions et tarifs affichés dans la plateforme.
                      </p>
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Nom (Français) <span className="text-danger">*</span></Label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Hôtel Atlas Sétif"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>الاسم (عربي) <span className="text-danger">*</span></Label>
                          <Input
                            name="name_ar"
                            value={formData.name_ar}
                            onChange={handleChange}
                            placeholder="فندق أطلس"
                            dir="rtl"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>État / Wilaya <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-map-marker-radius-outline" /></InputGroupText>
                            <Input
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              placeholder="Sétif"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Prix de départ <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <Input
                              type="number"
                              name="startprice"
                              value={formData.startprice}
                              onChange={handleChange}
                              placeholder="7500"
                            />
                            <InputGroupText>DA</InputGroupText>
                          </InputGroup>
                          <small className="text-muted">Prix minimum visible côté client.</small>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Description (Français) <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={5}
                            placeholder="Hôtel 4 étoiles situé au cœur de Sétif, à proximité du centre-ville…"
                          />
                          <small className="text-muted">{formData.description.length} caractères</small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>الوصف (عربي) <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            name="description_ar"
                            value={formData.description_ar}
                            onChange={handleChange}
                            rows={5}
                            placeholder="فندق 4 نجوم يقع في قلب مدينة سطيف…"
                            dir="rtl"
                          />
                          <small className="text-muted">{formData.description_ar.length} caractères</small>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 4. Location & map ──────────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-map-search-outline text-primary me-2" />
                        Emplacement
                      </h5>
                      <p className="text-muted mb-0">
                        Recherchez une adresse ou cliquez directement sur la carte pour positionner votre marqueur.
                      </p>
                    </div>

                    <FormGroup>
                      <InputGroup>
                        <InputGroupText><i className="mdi mdi-magnify" /></InputGroupText>
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Ex. Sétif, Algérie"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearch();
                            }
                          }}
                        />
                        <Button color="primary" type="button" onClick={handleSearch}>
                          <i className="mdi mdi-magnify me-1" /> Rechercher
                        </Button>
                      </InputGroup>
                    </FormGroup>

                    <div
                      style={{
                        height: 380,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid #e5e9f2",
                      }}
                    >
                      <MapContainer
                        center={formData.coordinates}
                        zoom={13}
                        scrollWheelZoom
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; OpenStreetMap'
                        />
                        <MapHandler
                          coordinates={formData.coordinates}
                          onCoordinateChange={(c) => setField("coordinates", c)}
                        />
                      </MapContainer>
                    </div>

                    <Row className="mt-3">
                      <Col md={8}>
                        <small className="text-muted d-block">Lieu sélectionné</small>
                        <strong>{formData.location || "—"}</strong>
                      </Col>
                      <Col md={4} className="text-md-end">
                        <small className="text-muted d-block">Coordonnées</small>
                        <code className="small">
                          {formData.coordinates[0].toFixed(4)}, {formData.coordinates[1].toFixed(4)}
                        </code>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 5. Images ──────────────────────────────────────── */}
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-image-multiple-outline text-primary me-2" />
                        Photos
                      </h5>
                      <p className="text-muted mb-0">
                        Ajoutez plusieurs images de qualité (vue extérieure, chambres, restaurant…).
                      </p>
                    </div>

                    <label
                      htmlFor="images"
                      className="d-block text-center border border-2 border-dashed rounded-3 p-4 mb-3"
                      style={{ cursor: "pointer", borderColor: "#cfd8e3" }}
                    >
                      <i className="mdi mdi-cloud-upload-outline display-6 text-primary mb-2" />
                      <p className="mb-1 fw-medium">Cliquez pour téléverser</p>
                      <small className="text-muted">JPG, PNG — plusieurs fichiers acceptés</small>
                      <input
                        type="file"
                        id="images"
                        name="images"
                        onChange={handleFileChange}
                        accept="image/*"
                        multiple
                        hidden
                      />
                    </label>

                    {imagePreview.length > 0 && (
                      <div className="d-flex flex-wrap gap-2">
                        {imagePreview.map((src, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: 100,
                              height: 100,
                              position: "relative",
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e5e9f2",
                            }}
                          >
                            <img
                              src={src}
                              alt={`Aperçu ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="btn btn-danger btn-sm"
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                width: 24,
                                height: 24,
                                padding: 0,
                                lineHeight: 1,
                                borderRadius: "50%",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      {imagePreview.length} image{imagePreview.length !== 1 ? "s" : ""} ajoutée
                      {imagePreview.length !== 1 ? "s" : ""}
                    </small>
                  </CardBody>
                </Card>
              </Col>

              {/* ─── 6. Services ──────────────────────────────────── */}
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-room-service-outline text-primary me-2" />
                        Services proposés
                      </h5>
                      <p className="text-muted mb-0">
                        Listez les services et équipements offerts (Wi-Fi, parking, piscine…).
                      </p>
                    </div>
                    <InputGroup className="mb-3">
                      <InputGroupText><i className="mdi mdi-plus" /></InputGroupText>
                      <Input
                        value={serviceInput}
                        onChange={(e) => setServiceInput(e.target.value)}
                        placeholder="Ex. Wi-Fi gratuit"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addService();
                          }
                        }}
                      />
                      <Button color="primary" type="button" onClick={addService}>
                        Ajouter
                      </Button>
                    </InputGroup>

                    {formData.services.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-clipboard-text-outline display-6 d-block mb-2" />
                        Aucun service pour le moment.
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {formData.services.map((s, idx) => (
                          <Badge
                            key={idx}
                            color="light"
                            className="d-inline-flex align-items-center text-dark border"
                            style={{
                              padding: "8px 12px",
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            <i className="mdi mdi-check-circle text-success me-1" />
                            {s}
                            <button
                              type="button"
                              onClick={() => removeService(idx)}
                              className="btn-close ms-2"
                              style={{ fontSize: 9 }}
                              aria-label="Supprimer"
                            />
                          </Badge>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-3">
                      {formData.services.length} service{formData.services.length !== 1 ? "s" : ""} ajouté
                      {formData.services.length !== 1 ? "s" : ""}
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 7. Submit bar ───────────────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col md={6}>
                        <small className="text-muted">
                          Vérifiez toutes les sections avant de soumettre.
                        </small>
                      </Col>
                      <Col md={6} className="text-md-end mt-3 mt-md-0">
                        <Button
                          color="light"
                          type="button"
                          onClick={() => (window.location.href = "/proprety")}
                          disabled={loading}
                        >
                          Annuler
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                          className="ms-2"
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" /> Enregistrement…
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-check-bold me-1" />
                              Créer la propriété
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(PropertyCreate);
